import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { App as AntdApp, ConfigProvider } from "antd";
import React from "react";
import ReactDOM from "react-dom/client";
import { useTranslation } from "react-i18next";
import { BrowserRouter } from "react-router-dom";

import { ApiError } from "./api/client";
import { App } from "./App";
import { AuthProvider } from "./auth/AuthProvider";
import "./i18n";
import { currentLocale } from "./i18n";
import { antdLocale, applyDayjsLocale } from "./i18n/antd";

import "antd/dist/reset.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A 401 means the session is over — retrying only delays the redirect to
      // the login screen. A 403 is a permission decision and will not change
      // either, and a 404 says the resource is not there — asking twice more
      // only turns one wrong answer into three. Everything else may be a
      // transient network problem.
      retry: (failureCount, error) => {
        if (
          error instanceof ApiError &&
          (error.status === 401 || error.status === 403 || error.status === 404)
        ) {
          return false;
        }
        return failureCount < 2;
      },
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
  queryCache: new QueryCache(),
});

function Root() {
  const { i18n } = useTranslation();
  const locale = currentLocale();

  applyDayjsLocale(locale);

  return (
    <ConfigProvider
      locale={antdLocale(locale)}
      // Die Titel der Abschnitts-Trenner stehen am linken Rand. Antd setzt
      // davor einen Anteil von 5 Prozent der Breite (Token orientationMargin);
      // bis Version 5 liess sich das mit orientationMargin={0} am Divider
      // abschalten, diese Eigenschaft ist inzwischen veraltet. Das Token ist die
      // aktuelle Fassung derselben Einstellung, und es gilt fuer alle Trenner
      // der Anwendung.
      theme={{ components: { Divider: { orientationMargin: 0 } } }}
      // Das Token nimmt der Linie links vom Titel die Breite, der Titel selbst
      // traegt aber noch antds Innenabstand von 1em auf beiden Seiten. Nach
      // links soll er weg, nach rechts bleibt er — sonst klebte der Text an der
      // Linie, die ihm folgt.
      divider={{ styles: { content: { paddingInlineStart: 0 } } }}
      key={i18n.language}
    >
      <AntdApp>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AuthProvider>
              <App />
            </AuthProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </AntdApp>
    </ConfigProvider>
  );
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>,
);
